import { useEffect, useState } from "react";
import * as d3 from "d3";
import { EndNotes } from "@/components/EndNotes";
import { PageContainer } from "@/components/PageContainer";
import { Select } from "@/components/Select";
import { useFilter } from "@/hooks/useFilter";
import { useUrls } from "@/hooks/useUrls";
import economyOptions from "@/config/product-concentration/economies";
import {
  formatPercentage,
  formatRows,
  getEconomyDisplayName,
  getSectorDisplayName,
  makePossessive,
} from "@/utils/format";
import { getSectorItems, getEconomyItems } from "@/utils/filter";
import { BubbleChart } from "./BubbleChart";
import { SuppliersChart } from "./SuppliersChart";
import styles from "./styles.module.scss";
const economyItems = getEconomyItems(economyOptions);

const schema = [
  { name: "Sector code" },
  { name: "Year" },
  { name: "Sub-sector code" },
  {
    name: "Total reporter value",
    format: (value: string) => parseInt(value),
  },
  {
    name: "Economy-specific HHI",
    format: (value: string) => parseInt(value),
  },
  {
    name: "Global HHI",
    format: (value: string) => parseInt(value),
  },
  {
    name: "Economy top 3 share",
    format: (value: string) => parseFloat(value),
  },
  {
    name: "Global top 3 share",
    format: (value: string) => parseFloat(value),
  },
] as const;

const getDataRows = (rows: unknown) => {
  return formatRows(rows, schema);
};

export type DataRow = ReturnType<typeof getDataRows>[number];

const suppliersSchema = [
  { name: "Partner code" },
  { name: "Year" },
  { name: "Sub-sector code" },
  {
    name: "Value",
    format: (value: string) => parseInt(value),
  },
  {
    name: "Share of total imports",
    format: (value: string) => parseFloat(value),
  },
] as const;

const getSuppliersDataRows = (rows: unknown) => {
  return formatRows(rows, suppliersSchema);
};

export type SuppliersDataRow = ReturnType<typeof getSuppliersDataRows>[number];

const comparisonSchema = [
  { name: "Sector code" },
  { name: "Year" },
  {
    name: "Economy- and sector-specific share of concentrated imports",
    format: (value: string) => parseFloat(value),
  },
  {
    name: "Economy-specific share of concentrated imports",
    format: (value: string) => parseFloat(value),
  },
] as const;

const getComparisonDataRows = (rows: unknown) => {
  return formatRows(rows, comparisonSchema);
};

type ComparisonDataRow = ReturnType<typeof getComparisonDataRows>[number];

export function App() {
  const urls = useUrls();
  const pageSlug = "product-concentration";
  const urlInfo = urls[pageSlug] || {};

  const [data, setData] = useState<DataRow[]>([]);
  const [suppliersData, setSuppliersData] = useState<SuppliersDataRow[]>([]);
  const [comparisonData, setComparisonData] = useState<ComparisonDataRow[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isSuppliersLoading, setIsSuppliersLoading] = useState(true);

  const { value: economy, onChange: onEconomyChange } = useFilter(
    "eco",
    economyOptions
  );

  const yearOptionsSet = new Set<string>();
  data.forEach((row) => {
    yearOptionsSet.add(row.Year);
  });
  const yearOptions = data.length
    ? Array.from(yearOptionsSet).sort((a, b) => parseInt(b) - parseInt(a))
    : [];

  const { value: year, onChange: onYearChange } = useFilter(
    "year",
    yearOptions
  );

  const sectorOptionsSet = new Set<string>();
  data.forEach((row) => {
    sectorOptionsSet.add(row["Sector code"].toLowerCase());
  });
  const sectorOptions = data.length
    ? Array.from(sectorOptionsSet).sort((a, b) => a.localeCompare(b))
    : [];
  const sectorItems = getSectorItems(sectorOptions);

  const { value: sector, onChange: onSectorChange } = useFilter(
    "sector",
    sectorOptions
  );

  const filteredData = data
    .filter(
      (d) => d["Sector code"].toLowerCase() === sector && d["Year"] === year
    )
    // Sort data so that bigger bubbles are drawn first in BubbleChart, which
    // allows clicking smaller bubbles inside of them (since those will be drawn
    // on top)
    .sort((a, b) => b["Total reporter value"] - a["Total reporter value"]);

  const productOptionsSet = new Set<string>();
  filteredData.forEach((row) => {
    productOptionsSet.add(row["Sub-sector code"]);
  });
  const productOptions = data.length
    ? Array.from(productOptionsSet).sort((a, b) => a.localeCompare(b))
    : [];
  const biggestProduct = filteredData[0]?.["Sub-sector code"];
  const { value: product, onChange: onProductChange } = useFilter(
    "product",
    productOptions,
    biggestProduct
  );

  useEffect(() => {
    setIsDataLoading(true);
    d3.csv(
      `${
        process.env.ASSET_PATH || ""
      }/data/product-concentration/${economy}.csv`
    )
      .then((data) => {
        setData(getDataRows(data));
      })
      .catch((e) => {
        console.error(e);
        setData([]);
      })
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [economy]);

  useEffect(() => {
    setIsSuppliersLoading(true);
    if (sector) {
      d3.csv(
        `${
          process.env.ASSET_PATH || ""
        }/data/product-concentration-suppliers/${economy}-${sector}.csv`
      )
        .then((data) => {
          setSuppliersData(getSuppliersDataRows(data));
        })
        .catch((e) => {
          console.error(e);
          setSuppliersData([]);
        })
        .finally(() => {
          setIsSuppliersLoading(false);
        });
    }
  }, [economy, sector]);

  useEffect(() => {
    d3.csv(
      `${
        process.env.ASSET_PATH || ""
      }/data/product-concentration-comparison/${economy}.csv`
    )
      .then((data) => {
        setComparisonData(getComparisonDataRows(data));
      })
      .catch((e) => {
        console.error(e);
        setComparisonData([]);
      });
  }, [economy]);

  const filteredSuppliersData = suppliersData.filter(
    (d) => d["Year"] === year && d["Sub-sector code"] === product
  );

  const displayEconomy = getEconomyDisplayName(economy);

  const comparisonDataRow = comparisonData.find(
    (d) => d["Sector code"].toLowerCase() === sector && d["Year"] === year
  );

  let allGoodsComparison = undefined;
  let comparison = undefined;

  if (comparisonDataRow) {
    const specificPercent = formatPercentage(
      comparisonDataRow[
        "Economy- and sector-specific share of concentrated imports"
      ] / 100
    );
    const globalPercent = formatPercentage(
      comparisonDataRow["Economy-specific share of concentrated imports"] / 100
    );
    const economyName = getEconomyDisplayName(economy);
    const sectorName = getSectorDisplayName(sector).toLowerCase();

    allGoodsComparison =
      comparisonDataRow && sector.toUpperCase() === "0AG"
        ? `${specificPercent} of all ${makePossessive(
            economyName
          )} imports is concentrated in ${year}.`
        : undefined;

    comparison =
      comparisonDataRow && sector.toUpperCase() !== "0AG"
        ? `${specificPercent} of ${makePossessive(
            economyName
          )} imports in the ${sectorName} sector is concentrated compared to ${globalPercent} across all ${makePossessive(
            economyName
          )} imports in ${year}.`
        : undefined;
  }

  return (
    <PageContainer
      headline={urlInfo.headline}
      dek={urlInfo.dek}
      footerCTASlug="home"
      activePageSlug={pageSlug}
      isLoading={isDataLoading || isSuppliersLoading}
      filters={
        <>
          <Select
            label="Economy"
            items={economyItems}
            value={economy}
            format="control-bar"
            onChange={(value) => {
              onEconomyChange(value);
            }}
          />
          <Select
            label="Sector"
            items={sectorItems}
            value={sector}
            format="control-bar"
            onChange={(value) => {
              onSectorChange(value);
            }}
          />
          <Select
            label="Year"
            items={yearOptions.map((o) => ({ value: o }))}
            value={year}
            format="control-bar"
            onChange={(value) => onYearChange(value)}
          />
        </>
      }
    >
      <div className={styles["bubble-chart-wrap"]}>
        <div className={styles["bubble-chart"]}>
          <BubbleChart
            data={filteredData}
            displayEconomy={displayEconomy}
            sector={sector}
            product={product}
            onProductChange={onProductChange}
            concentrationDescription={allGoodsComparison || comparison}
          />
        </div>

        <div className={styles["notes-container"]}>
          <EndNotes mode="collapsed">
            <p>
              <sup>1</sup>The Herfindahl-Hirschman Index (HHI) is a common
              measure of market concentration. For product level analysis, HHI
              values greater than 3,000 typically mean that there are 3 or fewer
              suppliers for the product globally (y-axis), or that a specific
              economy relies on 3 or few suppliers for its imports (x-axis).
              Regional calculations of HHI and share of concentrated trade
              exclude any trade between economies within the selected region.
              Largest products by global imports have been allocated shortened
              names. Original names can be found on the UN Comtrade website
              using the HS6 code. Products with only unspecified suppliers are
              not shown.
            </p>
            <p>Source: UN Comtrade (Refreshed 2025-01-30)</p>
          </EndNotes>
        </div>
      </div>

      <div className={styles["suppliers-chart"]}>
        <SuppliersChart
          data={filteredSuppliersData}
          displayEconomy={displayEconomy}
          product={product}
          productOptions={productOptions}
          onProductChange={onProductChange}
        />
      </div>

      <div className={styles["notes-container"]}>
        <EndNotes mode="collapsed">
          <p>
            Regional imports exclude any trade between economies within the
            selected region. Largest products by global imports have been
            allocated shortened names. Original names can be found on the UN
            Comtrade website using the HS6 code. All unspecified trade partners
            are aggregated under the “Not specified” label. See technical
            appendix for more details on how unspecified trade partners are
            treated.
          </p>
          <p>Source: UN Comtrade (Refreshed 2025-01-30)</p>
        </EndNotes>
      </div>
    </PageContainer>
  );
}
